import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProtectedFeature } from './components/ProtectedFeature';
import { Navbar } from './components/Navbar';
import { EmailVerificationBanner } from './components/EmailVerificationBanner';
import { Landing } from './pages/Landing';
import { Auth } from './pages/Auth';
import { Dashboard } from './pages/Dashboard';
import { MoodJournal } from './pages/MoodJournal';
import { Medications } from './pages/Medications';
import { Appointments } from './pages/Appointments';
import { Emergency } from './pages/Emergency';
import { JustTalk } from './pages/JustTalk';
import { Store } from './pages/Store';
import { Profile } from './pages/Profile';
import { Success } from './pages/Success';
import { UserManagement } from './pages/UserManagement';
import { UserChats } from './pages/UserChats';
import { VerifyEmail } from './pages/VerifyEmail';
import { AboutUs } from './pages/AboutUs';
import { ContactUs } from './pages/ContactUs';
import { Amigo } from './pages/Amigo';
import { Forums } from './pages/Forums';
import { ForumModeration } from './pages/ForumModeration';
import { DoctorSummary } from './pages/DoctorSummary';
import { PrivateVault } from './pages/PrivateVault';
import { PlanAndPricing } from './pages/PlanAndPricing';
import { DiscountCodes } from './pages/DiscountCodes';
import Settings from './pages/Settings';
import ContentLibrary from './pages/ContentLibrary';
import ResetPassword from './pages/ResetPassword';
import DoctorDashboard from './pages/DoctorDashboard';
import GroupTherapy from './pages/GroupTherapy';
import QuantumManifestation from './pages/QuantumManifestation';
import VideoConsultation from './pages/VideoConsultation';
import { Footer } from './components/Footer';
import './App.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App min-h-screen flex flex-col">
            <Navbar />
            <EmailVerificationBanner />
            <Toaster position="top-right" richColors />
            <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/success" element={<Success />} />
            <Route path="/cancel" element={<Navigate to="/dashboard" replace />} />
            
            {/* Video Consultation Route */}
            <Route path="/video/:roomId" element={
              <ProtectedRoute>
                <VideoConsultation />
              </ProtectedRoute>
            } />
            
            {/* Doctor Portal Route */}
            <Route path="/doctor" element={
              <ProtectedRoute>
                <DoctorDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            {/* FREE Features - Available to all users */}
            <Route path="/mood-journal" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="mood">
                  <MoodJournal />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/appointments" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="appointments">
                  <Appointments />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/store" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="store">
                  <Store />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/emergency" element={
              <ProtectedRoute>
                <ProtectedFeature feature="emergency">
                  <Emergency />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/content" element={
              <ProtectedRoute>
                <ContentLibrary />
              </ProtectedRoute>
            } />
            
            {/* PREMIUM Features - Require subscription */}
            <Route path="/medications" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="medications">
                  <Medications />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/talk" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="talk">
                  <JustTalk />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/amigo" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="ai_companion">
                  <Amigo />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/forums" element={
              <ProtectedRoute>
                <Forums />
              </ProtectedRoute>
            } />
            <Route path="/group-therapy" element={
              <ProtectedRoute>
                <GroupTherapy />
              </ProtectedRoute>
            } />
            <Route path="/quantum-manifestation" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="mindpulse">
                  <QuantumManifestation />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/doctor-summary" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="doctor_summary">
                  <DoctorSummary />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            <Route path="/vault" element={
              <ProtectedRoute requireVerification>
                <ProtectedFeature feature="private_vault">
                  <PrivateVault />
                </ProtectedFeature>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/chats" element={
              <ProtectedRoute>
                <UserChats />
              </ProtectedRoute>
            } />
            <Route path="/admin/forum-moderation" element={
              <ProtectedRoute>
                <ForumModeration />
              </ProtectedRoute>
            } />
            <Route path="/admin/discount-codes" element={
              <ProtectedRoute>
                <DiscountCodes />
              </ProtectedRoute>
            } />
            
            {/* Static Pages */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/pricing" element={<PlanAndPricing />} />
          </Routes>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;